import type {MediaTypeLoadingStates, MediaTypePublishingParams, PostState, SocialMediaAccountId, SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {noop} from '@Utils/general.util';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useLatest} from 'ahooks';
import {runAutoMetricsRefreshBatch} from '@Libraries/social-media-metrics-refresh.library';
import {
  doPublishForAccount,
  getPostableSocialMediaAccountsForUser,
  getSocialMediaAccountsForUser,
  getSocialMediaPostsForUser,
  updateSocialMediaPostPublishingParams,
} from '@Components/social-media/social-media-thunk';
import {
  DEFAULT_POSTS_LIMIT,
  markPostsAsRefreshing,
  resetPosts,
  updateAccountSorter,
  updateDoRefetchGridForNewSorting,
  updatePostSorter,
} from '@Components/social-media/social-media-reducer';
import type {AccountState, SocialAccount} from '@Components/social-media/account.vo';
import type {
  AccountStateFilter,
  ContainerQueryParams,
  DesignSelection,
  PostStateFilter,
  PublishedUrlMapForAccount,
  RetryStatusForMediaType,
  SocialPlatform,
  SocialPostGridContainerQuery,
} from '@Components/social-media/social-media.types';
import {AccountSortingMetric, PostSortingMetric, socialGridContainerQuery} from '@Components/social-media/social-media.types';
import type {ListItemStorage} from '@Components/list/list.types';
import {LIST_ITEM_TYPE} from '@Components/list/list.types';
import {isPostVisibleForFilter} from '@Libraries/social-media-post.library';
import {buildGridRequestParams, isGridScrolledToBottom, LAZY_LOAD_DEBOUNCE_MS, SOCIAL_POSTS_GRID_CONTAINER_ID} from '@Libraries/social-media-grid.library';
import {isPromotingEvent, trackEventPromotionScheduledForPlatform} from '@Libraries/social-media-event-promotion.library';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {executeThunk} from '@Utils/thunk.util';
import type {ErrorResponseUpdatePublishingParams} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import type {SocialMediaPublishingParams} from '@Components/social-media/publish-params.types';
import {RetryStatus, SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {LoadingStates} from '@Utils/loading.util';
import {
  initPublishingStatusMapForAccounts,
  unsetRetryingStatusForAccountWithMediaType,
  updateGraphicsReadiness,
  updatePublishingStatusForAccountWithMediaType,
  updatePublishingStatusForAllAccountMediaTypes,
  updateSavingPublishingParamsState,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {pollPublishGraphicsReadiness} from '@Components/social-media/social-media-graphics-readiness-thunk';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {AccountResizeStatus} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import {useDesignsToPublish} from '@Components/social-media-wizard/social-media-wizard.hooks';
import type {StoryGuideline} from '@Components/social-media-wizard/components/social-media-publish-params-story-guideline';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {getPublishedSlotCounts, haveAllPublishingStatesLoaded, haveAllPublishingStatesSettled} from '@Libraries/social-media-wizard-accounts-library';
import type {PublishedSlotCounts} from '@Libraries/social-media-wizard-accounts-library';
import {buildScheduleTrackingEvents, getPlatformForAccount, handlePublishErrorResponse, handlePublishSuccessResponse, handleUpdatePublishingParamsError} from '@Libraries/social-media-publishing.library';
import {getSanitizedTimeZoneOffset} from '@Libraries/social-media-wizard-scheduling-library';

export const useSocialMediaPosts = (): SocialPost[] => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.posts;
  });
};

export const useSocialMediaHaveAccountsLoaded = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.haveAccountsLoaded;
  });
};

export const useSocialMediaHavePostsLoaded = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.havePostsLoaded;
  });
};

export const useDoRefetchGridForNewSorting = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.doRefetchGridForNewSorting;
  });
};

export const useGetSharedInTeamPosts = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.getSharedInTeamPosts;
  });
};

export const useSocialMediaPostsOffset = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.posts.length;
  });
};

export const useAccountStateFilter = (): AccountStateFilter => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accountStateFilter;
  });
};

export const useIsAccountVisibleForFilter = (accountState: AccountState): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accountStateFilter[accountState];
  });
};

export const useSocialPostGridContainerQuery = (): ContainerQueryParams => {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, socialGridContainerQuery);

  const containerQueryParamProps: SocialPostGridContainerQuery = {
    xsmall: params.xsmall,
    small: params.small,
    medium: params.medium,
    large: params.large,
    xlarge: params.xlarge,
    xxlarge: params.xxlarge,
  };

  return {
    containerQueryParamProps,
    containerRef,
    hasContainerQueryInitialized: params.small !== undefined,
  };
};

export const useIsPostVisibleForFilter = (postState: PostState): boolean => {
  const postStateFilter = useAppSelector((state) => {
    return state.socialMediaGrid.postStateFilter;
  });

  const [isVisible, setIsVisible] = useState(true);

  useDidUpdateEffect(() => {
    setIsVisible(isPostVisibleForFilter(postState, postStateFilter));
  }, [postStateFilter]);

  return isVisible;
};

export const usePostStateFilter = (): PostStateFilter => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.postStateFilter;
  });
};

export const useSearchTerm = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.searchTerm;
  });
};

export const usePostSorter = (): PostSortingMetric => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.postSorter;
  });
};

export const useMorePostsOnServer = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.morePostsOnServer;
  });
};

export const useGetDeletedPosts = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.getDeletedPosts;
  });
};
export const useAccountSorter = (): AccountSortingMetric => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accountSorter;
  });
};

export const useSocialMediaAccounts = (): SocialAccount[] => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accounts;
  });
};

export const useLoadSocialAccountsForWizard = (): void => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    void executeThunk(() => {
      return dispatch(getPostableSocialMediaAccountsForUser());
    }).then(noop);
  }, []);
};

export const usePublishingStatusForAllAccounts = (): Record<SocialMediaAccountId, MediaTypeLoadingStates> => {
  return useAppSelector((state): Record<SocialMediaAccountId, MediaTypeLoadingStates> => {
    return state.socialMediaWizard.publishingStatusForMediaTypes;
  });
};

export const usePublishingStatusForAccountWithMediaType = (idSocialMediaAccount: SocialMediaAccountId, mediaType: PlatformMediaType): LoadingStates => {
  return useAppSelector((state) => {
    if (state.socialMediaWizard.publishingStatusForMediaTypes[idSocialMediaAccount] === undefined) {
      return LoadingStates.LOADING;
    }

    return state.socialMediaWizard.publishingStatusForMediaTypes[idSocialMediaAccount][mediaType];
  });
};

export const useRetryStatusForAccount = (idSocialMediaAccount: SocialMediaAccountId): RetryStatusForMediaType => {
  return useAppSelector((state): RetryStatusForMediaType => {
    return state.socialMediaWizard.retryPublishingMapForAccount[idSocialMediaAccount];
  });
};

export const useHaveAllPostsPublished = (): boolean => {
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const publishingParams = usePublishingParams();

  return haveAllPublishingStatesLoaded(accountIdsToPublishTo, publishingStatusForAllAccounts, publishingParams);
};

export const useHaveAllPostsSettled = (): boolean => {
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();

  return haveAllPublishingStatesSettled(accountIdsToPublishTo, publishingStatusForAllAccounts);
};

export const usePublishedSlotCounts = (): PublishedSlotCounts => {
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const publishingParams = usePublishingParams();

  return getPublishedSlotCounts(accountIdsToPublishTo, publishingStatusForAllAccounts, publishingParams);
};

/**
 * Unified retry hook for all media types (POST, REELS, STORIES).
 * All hooks are called unconditionally at the top; guards run inside the effect.
 * The caller passes the exact platformMediaType it is rendering for, so the
 * hook's trigger is per-(account, mediaType) while retryStatusForAccount holds
 * the full Record<PlatformMediaType, RetryStatus | null> map.
 */
export const useRetryPublishingForAccount = (idSocialMediaAccount: SocialMediaAccountId, platformMediaType: PlatformMediaType): void => {
  const dispatch = useAppDispatch();
  const retryStatusForAccount = useRetryStatusForAccount(idSocialMediaAccount);
  const post = useSocialMediaWizardPost();
  const wizardAccounts = useSocialMediaWizardAccounts();

  useEffect(() => {
    if (!retryStatusForAccount) {
      console.error('No retry status for account with id ', idSocialMediaAccount, ' found');
      return;
    }

    const retryStatusForMediaType = retryStatusForAccount[platformMediaType];
    if (!retryStatusForMediaType || retryStatusForMediaType !== RetryStatus.RETRYING) {
      return;
    }

    const resetRetryStatusOnCompletion = (): void => {
      dispatch(unsetRetryingStatusForAccountWithMediaType({accountId: idSocialMediaAccount, platformMediaType}));
    };

    const onError = (): void => {
      handlePublishErrorResponse({dispatch, idSocialMediaAccount, platformMediaType, resetUrl: false});
      resetRetryStatusOnCompletion();
    };

    void executeThunk(
      () => {
        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADING,
            platformMediaType,
          })
        );

        return dispatch(
          doPublishForAccount({
            idPost: post ? post.id : null,
            idSocialMediaAccount,
            isRetry: true,
            mediaType: platformMediaType,
          })
        );
      },
      (data: object) => {
        handlePublishSuccessResponse({
          dispatch,
          idSocialMediaAccount,
          platformMediaType,
          data,
          platform: getPlatformForAccount(wizardAccounts, idSocialMediaAccount),
          onError,
        });
        resetRetryStatusOnCompletion();
      },
      () => {
        onError();
      }
    );
  }, [retryStatusForAccount]);
};

/**
 * Unifies Reel/Post and Story publishing: every hook is called unconditionally at the top, and the
 * media-type-specific guards (including the publishingParams existence check that applies only to
 * non-STORIES types) live inside the effect body.
 */
export const useStartPublishingForAccount = (idSocialMediaAccount: SocialMediaAccountId, platformMediaType: PlatformMediaType): void => {
  const dispatch = useAppDispatch();
  const savingPublishingParamsState = useSavingPublishingParamsState();
  const publishingAction = useSocialPostPublishingAction();
  const publishingParams = usePublishingParams();
  const publishingState = usePublishingStatusForAccountWithMediaType(idSocialMediaAccount, platformMediaType);
  const post = useSocialMediaWizardPost();
  const wizardAccounts = useSocialMediaWizardAccounts();

  useEffect(() => {
    if (publishingState === LoadingStates.LOADED || publishingState === LoadingStates.NOT_LOADED) {
      return;
    }

    if (savingPublishingParamsState !== LoadingStates.LOADED) {
      return;
    }

    if (publishingAction !== SocialPostPublishingAction.PUBLISH) {
      return;
    }

    // Existence guard preserved only for non-STORIES (matching the original reel/post hook); now runs inside the effect, not every render, per the rules-of-hooks fix.
    // Optional chaining is an intentional safety upgrade over the original's bare bracket access: a missing account slot now bails+logs instead of throwing (same outcome: no publish).
    if (platformMediaType !== PlatformMediaType.STORIES && !publishingParams[idSocialMediaAccount]?.[platformMediaType]) {
      console.error('No Publishing Params found for account with id: ', idSocialMediaAccount, ' and platform media type: ', platformMediaType);
      return;
    }

    const onError = (): void => {
      handlePublishErrorResponse({dispatch, idSocialMediaAccount, platformMediaType, resetUrl: true});
    };

    void executeThunk(
      () => {
        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADING,
            platformMediaType,
          })
        );

        return dispatch(
          doPublishForAccount({
            idPost: post ? post.id : null,
            idSocialMediaAccount,
            mediaType: platformMediaType,
            isRetry: false,
          })
        );
      },
      (data: object) => {
        handlePublishSuccessResponse({
          dispatch,
          idSocialMediaAccount,
          platformMediaType,
          data,
          platform: getPlatformForAccount(wizardAccounts, idSocialMediaAccount),
          onError,
        });
      },
      () => {
        onError();
      }
    );
  }, [savingPublishingParamsState]);
};

export const useUpdatePublishingParams = (): void => {
  const dispatch = useAppDispatch();
  const post = useSocialMediaWizardPost();
  const publishingAction = useSocialPostPublishingAction();
  const step = useWizardStep();
  const designs = useDesignsToPublish();
  const publishingParams = usePublishingParams();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();

  const updatePublishingStatusForSaveOrSchedule = (loadingState: LoadingStates): void => {
    if (publishingAction !== SocialPostPublishingAction.PUBLISH) {
      dispatch(updatePublishingStatusForAllAccountMediaTypes(loadingState));
    }
  };

  /**
   * Ordering guarantee for the publish fan-out: re-derives every account's statuses from the just-saved params
   * in the same tick savingPublishingParamsState flips to LOADED (the signal useStartPublishingForAccount fires
   * on), independent of PublishingPost child-mount order. The evidence-map rule in getInitialPublishStatusForMediaType
   * is what actually prevents a stale LOADED from skipping a slot; this makes that re-derive happen before the fire.
   */
  const armPublishingStatusesForPublish = (): void => {
    if (publishingAction === SocialPostPublishingAction.PUBLISH) {
      dispatch(initPublishingStatusMapForAccounts());
    }
  };

  const fireMultiPlatformPublishEvent = (): void => {
    if (!post || publishingAction !== SocialPostPublishingAction.PUBLISH) {
      return;
    }

    const numPlatforms = accountIdsToPublishTo.length;
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_NUMPLATFORMS, {[GA4EventParamName.NUM_PLATFORMS]: `${numPlatforms}`});
  };

  const fireScheduleTrackingEvent = (): void => {
    const events = buildScheduleTrackingEvents(post, publishingParams, publishingAction);
    events.forEach(({platformName, isStory}) => {
      if (isStory) {
        const storyPlatformForGA = `${platformName}_${PlatformMediaType.STORIES}`;
        trackPublishingGA4Events(GA4EventName.POST_SCHEDULED, {[GA4EventParamName.PLATFORM]: storyPlatformForGA});

        if (isPromotingEvent()) {
          trackEventPromotionScheduledForPlatform(platformName);
        }
      } else {
        trackPublishingGA4Events(GA4EventName.POST_SCHEDULED, {[GA4EventParamName.PLATFORM]: platformName});
        if (isPromotingEvent()) {
          trackEventPromotionScheduledForPlatform(platformName);
        }
      }
    });
  };

  const firePublishingParams = (): void => {
    updatePublishingStatusForSaveOrSchedule(LoadingStates.LOADING);
    dispatch(updateSavingPublishingParamsState(LoadingStates.LOADING));
    const publishOn: number | undefined = post ? post.publishOn : undefined;
    const timeZoneOffset: number | null = getSanitizedTimeZoneOffset(post);

    void executeThunk(
      () =>
        dispatch(
          updateSocialMediaPostPublishingParams({
            idPost: post ? post.id : null,
            designs: designs || null,
            publishOn: publishOn ?? null,
            timeZoneOffset,
            publishingParams,
          })
        ),
      () => {
        fireScheduleTrackingEvent();
        fireMultiPlatformPublishEvent();
        updatePublishingStatusForSaveOrSchedule(LoadingStates.LOADED);
        armPublishingStatusesForPublish();
        dispatch(updateSavingPublishingParamsState(LoadingStates.LOADED));
      },
      (response: ErrorResponseUpdatePublishingParams) => {
        const {error, data} = response;
        if (handleUpdatePublishingParamsError({error, data, dispatch, accountIdsToPublishTo})) {
          return;
        }

        fireMultiPlatformPublishEvent();
        updatePublishingStatusForSaveOrSchedule(LoadingStates.NOT_LOADED);
        dispatch(updateSavingPublishingParamsState(LoadingStates.NOT_LOADED));
      }
    );
  };

  useEffect(() => {
    if (step !== SocialMediaWizardStep.PUBLISH) {
      return;
    }

    if (publishingAction !== SocialPostPublishingAction.PUBLISH) {
      firePublishingParams();
      return;
    }

    let cancelled = false;
    const idPost = post?.id;

    if (!idPost) {
      firePublishingParams();
      return;
    }

    const runWithReadinessGate = async (): Promise<void> => {
      // pollPublishGraphicsReadiness sets LOADING from its instant kickoff, and only when graphics are
      // actually still pending — so a ready design never shows the "preparing" flash.
      try {
        await dispatch(pollPublishGraphicsReadiness({idPost})).unwrap();
      } catch {
        // timeout or error — proceed anyway; backend gate + enqueue pre-render will recover
      }

      if (cancelled) {
        dispatch(updateGraphicsReadiness(LoadingStates.NOT_LOADED));
        return;
      }

      dispatch(updateGraphicsReadiness(LoadingStates.LOADED));
      firePublishingParams();
    };

    void runWithReadinessGate();

    return (): void => {
      cancelled = true;
    };
  }, [step]);
};

export const useSocialMediaWizardPost = (): SocialPost | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.post;
  });
};

export const useSocialPostPublishingAction = (): SocialPostPublishingAction | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingAction;
  });
};

export const useSocialMediaWizardAccounts = (): SocialAccount[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accounts;
  });
};

export const useAccountsToPublishTo = (): SocialAccount[] => {
  const accounts = useSocialMediaWizardAccounts();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();

  return accounts.filter((account) => accountIdsToPublishTo.includes(account.id));
};

export const useHasCommonCaption = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.hasCommonCaption;
  });
};

export const useHasCommonPostCover = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.hasCommonPostCover;
  });
};

export const useCommonCaption = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.commonCaption;
  });
};

export const useTriggerNext = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.triggerNext;
  });
};

export const useTriggerPrevious = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.triggerPrevious;
  });
};

export const useAccountIdsToPublishTo = (): number[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accountIdsToPublishTo;
  });
};

export const usePostPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): SocialMediaPublishingParams | null => {
  return useAppSelector((state) => {
    if (!state.socialMediaWizard.publishingParams[socialMediaAccountId]) {
      return null;
    }

    return state.socialMediaWizard.publishingParams[socialMediaAccountId].POST;
  });
};

export const useReelPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): SocialMediaPublishingParams | null => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingParams[socialMediaAccountId].REELS;
  });
};

export const useStoryPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): SocialMediaPublishingParams | null => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingParams[socialMediaAccountId].STORIES;
  });
};

export const useAllPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): MediaTypePublishingParams => {
  return useAppSelector((state): MediaTypePublishingParams => {
    return state.socialMediaWizard.publishingParams[socialMediaAccountId];
  });
};

export const useStoryGuidelineForAccount = (accountId: number): StoryGuideline => {
  return useAppSelector((state): StoryGuideline => {
    return state.socialMediaWizard.storyGuidelineForAccount[accountId];
  });
};

export const usePublishingParams = (): Record<SocialMediaAccountId, MediaTypePublishingParams> => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingParams;
  });
};

export const useDesignToResize = (): DesignSelection | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.designToResize;
  });
};

export const useAccountTypeToConnect = (): SocialPlatform | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accountTypeToConnect;
  });
};

export const useNextButtonIcon = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.nextButtonIcon;
  });
};

export const useNextButtonText = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.nextButtonText;
  });
};

export const useEmphasizedPlatform = (): SocialPlatform | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.emphasizedPlatform;
  });
};

export const useNextButtonDisabled = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.nextButtonDisabled;
  });
};

export const useSavingPublishingParamsState = (): LoadingStates => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.savingPublishingParamsState;
  });
};

export const useWizardStep = (): SocialMediaWizardStep => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.wizardStep;
  });
};

export const useLoadSocialAccounts = (): void => {
  const haveAccountsLoaded = useSocialMediaHaveAccountsLoaded();
  const dispatch = useAppDispatch();
  const isUserLogged = isUserLoggedIn();

  useEffect(() => {
    if (haveAccountsLoaded || !isUserLogged) {
      return;
    }

    void executeThunk(() => {
      return dispatch(getSocialMediaAccountsForUser());
    }).then(noop);
  }, [isUserLogged]);
};

export const useReloadGridForSorterIfNeeded = (): void => {
  const sortPostsBy = usePostSorter();
  const morePostsOnServer = useMorePostsOnServer();
  const numPostsLoaded = useSocialMediaPostsOffset();
  const getSharedInTeam = useGetSharedInTeamPosts();
  const dispatch = useAppDispatch();

  useDidUpdateEffect(() => {
    if (!morePostsOnServer) {
      return;
    }

    dispatch(updateDoRefetchGridForNewSorting(true));
    dispatch(resetPosts());

    const args = buildGridRequestParams({
      limit: numPostsLoaded,
      offset: 0,
      getDeleted: false,
      sortPostsBy,
      getSharedInTeam,
    });

    void executeThunk(() => {
      return dispatch(getSocialMediaPostsForUser(args));
    }).then();
  }, [sortPostsBy]);
};
export const useLoadSocialPosts = (getDeleted: boolean, getSharedInTeam: boolean): void => {
  const dispatch = useAppDispatch();
  const offset = useSocialMediaPostsOffset();
  const doRefetchGridForNewSorting = useDoRefetchGridForNewSorting();
  const havePostsLoaded = useSocialMediaHavePostsLoaded();
  const sortPostsBy = usePostSorter();
  const limit = DEFAULT_POSTS_LIMIT;

  const getPostsFromServer = async (): Promise<void> => {
    const args = buildGridRequestParams({limit, offset, getDeleted, sortPostsBy, getSharedInTeam});

    await executeThunk(() => {
      return dispatch(getSocialMediaPostsForUser(args));
    });
  };

  useEffect(() => {
    if (havePostsLoaded || doRefetchGridForNewSorting) {
      return;
    }

    void getPostsFromServer().then(noop);
  }, [havePostsLoaded]);
};

export const useLazyLoadPostsFromServer = (lazyloadFunction: VoidFunction): void => {
  const morePostsOnServer = useMorePostsOnServer();

  useEffect(() => {
    window.addEventListener('scroll', doLazyloadIfNeeded, {passive: true});

    return (): void => {
      window.removeEventListener('scroll', doLazyloadIfNeeded);
    };
  }, [morePostsOnServer]);

  const doLazyloadIfNeeded = window.PMW.debounce(() => {
    if (!morePostsOnServer) {
      return;
    }

    if (isGridScrolledToBottom(SOCIAL_POSTS_GRID_CONTAINER_ID)) {
      lazyloadFunction();
    }
  }, LAZY_LOAD_DEBOUNCE_MS) as VoidFunction;
};

export const useIsPostRefreshing = (postId: string): boolean => {
  return useAppSelector((state) => state.socialMediaGrid.refreshingPostIds[postId] ?? false);
};

/**
 * Auto-refreshes metrics for the grid's stale published posts. Each ID is kicked
 * off at most once per mount (lazy-loading refreshes only the new posts) via one
 * kickoff + one long-poll loop over the whole batch, cancelled on unmount. Each
 * post clears its skeleton the moment it settles — nothing ready is held back.
 * Premium-only.
 *
 * `refreshablePostIds` arrive in the grid's render order, so the backend enqueues
 * the stale posts top-first (a best-effort nudge toward above-the-fold settling
 * sooner). They're the posts the grid already determined are stale, so we
 * optimistically show their skeleton BEFORE paint (a layout effect) — the grid
 * never flashes stale numbers and then a skeleton. After the kickoff, posts the
 * server didn't enqueue revert to their numbers.
 */
export const useAutoRefreshMetricsForVisiblePosts = (refreshablePostIds: string[]): void => {
  const dispatch = useAppDispatch();
  const posts = useSocialMediaPosts();
  const postsRef = useLatest(posts);
  const dialogPostId = useAppSelector((state) => state.socialPostDialog.post?.id ?? null);
  const dialogPostIdRef = useLatest(dialogPostId);
  const kickedOffRef = useRef<Set<string>>(new Set());
  const activeEventIdsRef = useRef<Set<string>>(new Set());
  const eventIdCounterRef = useRef(0);
  const abortedRef = useRef(false);
  const havePostsLoaded = useSocialMediaHavePostsLoaded();
  const prevHavePostsLoadedRef = useRef(havePostsLoaded);

  useEffect(() => {
    // Full-grid reset (sort/team/trash toggle) empties posts and flips havePostsLoaded false: forget kicked-off IDs so the reloaded page is re-evaluated. Lazy-load pagination also flips havePostsLoaded false but keeps the loaded posts, so the empty-posts guard skips it — already-kicked-off rows aren't re-kicked. Backend guard still dedupes within the freshness window.
    if (prevHavePostsLoadedRef.current && !havePostsLoaded && postsRef.current.length === 0) {
      kickedOffRef.current.clear();
    }
    prevHavePostsLoadedRef.current = havePostsLoaded;
  }, [havePostsLoaded]);

  useLayoutEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    const newPostIds = refreshablePostIds.filter((id) => !kickedOffRef.current.has(id));
    if (newPostIds.length > 0) {
      dispatch(markPostsAsRefreshing(newPostIds));
    }
  }, [refreshablePostIds]);

  useEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    const newPostIds = refreshablePostIds.filter((id) => !kickedOffRef.current.has(id));
    if (newPostIds.length === 0) {
      return;
    }

    newPostIds.forEach((id) => kickedOffRef.current.add(id));
    eventIdCounterRef.current += 1;
    const eventId = `social-metrics-refresh-${eventIdCounterRef.current}`;
    activeEventIdsRef.current.add(eventId);

    void runAutoMetricsRefreshBatch(newPostIds, eventId, dispatch, postsRef, dialogPostIdRef, abortedRef).finally(() => {
      activeEventIdsRef.current.delete(eventId);
    });
  }, [refreshablePostIds]);

  useEffect(() => {
    return (): void => {
      abortedRef.current = true;
      activeEventIdsRef.current.forEach((eventId) => {
        document.dispatchEvent(new Event(window.PMW.getPollingStopEvent(eventId) as string));
      });
    };
  }, []);
};

export const useAccountsSorterListItems = (): ListItemStorage[] => {
  const dispatch = useAppDispatch();

  const accountSorter = useAccountSorter();
  return [
    {
      id: AccountSortingMetric.DATE_CREATED as string,
      isSelected: accountSorter === AccountSortingMetric.DATE_CREATED,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_date_added'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (AccountSortingMetric.DATE_CREATED as string)) {
          dispatch(updateAccountSorter(AccountSortingMetric.DATE_CREATED));
        }
      },
    },
    {
      id: AccountSortingMetric.BY_PLATFORM as string,
      isSelected: accountSorter === AccountSortingMetric.BY_PLATFORM,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_platform'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (AccountSortingMetric.BY_PLATFORM as string)) {
          dispatch(updateAccountSorter(AccountSortingMetric.BY_PLATFORM));
        }
      },
    },
  ];
};
export const usePostsSorterListItems = (): ListItemStorage[] => {
  const dispatch = useAppDispatch();

  const postSorter = usePostSorter();
  return [
    {
      id: PostSortingMetric.LAST_MODIFIED as string,
      isSelected: postSorter === PostSortingMetric.LAST_MODIFIED,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_last_modified'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (PostSortingMetric.LAST_MODIFIED as string)) {
          dispatch(updatePostSorter(PostSortingMetric.LAST_MODIFIED));
        }
      },
    },
    {
      id: PostSortingMetric.DATE_PUBLISHED as string,
      isSelected: postSorter === PostSortingMetric.DATE_PUBLISHED,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_publishing_date'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (PostSortingMetric.DATE_PUBLISHED as string)) {
          dispatch(updatePostSorter(PostSortingMetric.DATE_PUBLISHED));
        }
      },
    },
  ];
};

export const useAccountResizeCompletedStatusForPosters = (account: SocialAccount | undefined, platformMediaType: PlatformMediaType): AccountResizeStatus => {
  return useAppSelector((state): AccountResizeStatus => {
    if (!account) {
      return AccountResizeStatus.NOT_RESIZED;
    }

    const resizingStatusForDesigns = state.socialMediaWizard.accountResizeStatusMapForMediaType[account.id][platformMediaType];

    if (!resizingStatusForDesigns) {
      return AccountResizeStatus.NOT_RESIZED;
    }

    for (const resizePageMap of Object.values(resizingStatusForDesigns)) {
      for (const resizeInformation of Object.values(resizePageMap)) {
        if (resizeInformation !== AccountResizeStatus.RESIZE_DONE) {
          return resizeInformation;
        }
      }
    }

    return AccountResizeStatus.RESIZE_DONE;
  });
};

export const usePublishedAccountUrlMap = (): PublishedUrlMapForAccount => {
  return useAppSelector((state): PublishedUrlMapForAccount => {
    return state.socialMediaWizard.publishedAccountUrlMapForMediaType;
  });
};

export const useHasAccounts = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accounts.length > 0;
  });
};
